import { z } from 'zod'
import { parseWithZod } from '../utils'
import { auth } from './auth'
import { http } from './http'
import { DealTemplatesResponseSchema, DealTemplateResponseSchema } from '../schemas'
import type { DealTemplate, DealUsership, Region } from '../types'

const UpdateDealTemplateResponseSchema = z.object({
  deal_usership: z.object({
    id: z.number(),
    is_trashed: z.boolean(),
  }),
})

type RegionUserships = {
  region: Region
  userships: DealUsership[]
}

export type DealTemplateWithUsership = DealTemplate & {
  region_host: string
  usership_id: number | null
  is_trashed: boolean
  user_last_accessed: string | null
  tfa_required: boolean
}

export const dealTemplates = {
  // Templates for the deal's own region — dealroom resolves that from the URL
  getAll: async () => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deal_templates`)
    return parseWithZod(DealTemplatesResponseSchema, result).deal_templates
  },

  /**
   * Templates across every region the user has an account in, for the overview
   * app's Templates tab. `/api/deal_templates` only ever returns the templates
   * held in the region that served the request, and external users have no
   * subscription region for `auth.getUserRegionHost()` to resolve — without an
   * explicit host their requests go nowhere. Mirrors `deals.getAll`.
   */
  getAllByRegions: async (regionUserships: RegionUserships[]) => {
    const templatesByRegion = await Promise.all(
      regionUserships.map(({ region, userships }) => fetchRegionDealTemplates(region, userships)),
    )
    return templatesByRegion.flat()
  },

  getById: async (id: number, regionHost?: string) => {
    const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
    const result = await http.get(`${host}/api/deal_templates/${id}`)
    return parseWithZod(DealTemplateResponseSchema, result).deal_template
  },

  getAdminTemplates: async () => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.get(`${regionHost}/api/deal_templates/org_admin_list`)
    return parseWithZod(DealTemplatesResponseSchema, result).deal_templates
  },

  updateArchived: async ({
    template_id,
    is_being_archived,
    deal_id,
    regionHost,
  }: {
    template_id: number
    is_being_archived: boolean
    deal_id: number
    regionHost?: string
  }) => {
    const host = regionHost ? `${location.protocol}//${regionHost}` : auth.getUserRegionHost()
    const endpoint = `${host}/api/deal_templates/${template_id}/${is_being_archived ? 'archive' : 'unarchive'}`
    const result = await http.post(endpoint, undefined, deal_id)
    return parseWithZod(UpdateDealTemplateResponseSchema, result)
  },

  editInfo: async (payload: { id: number; title: string; preamble?: string | null }) => {
    const { id, ...body } = payload
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deal_templates/${id}/edit_template_deal_info`,
      body,
    )
    return parseWithZod(DealTemplateResponseSchema, result).deal_template
  },

  enableOrgAccess: async (dealTemplateId: number) => {
    const regionHost = auth.getUserRegionHost()
    const result = await http.post(
      `${regionHost}/api/deal_templates/${dealTemplateId}/enable_org_access`,
    )
    return parseWithZod(DealTemplateResponseSchema, result).deal_template
  },

  // Disabling is asynchronous server-side: the response is empty and the updated
  // deal template arrives later through live sync
  disableOrgAccess: async (dealTemplateId: number) => {
    const regionHost = auth.getUserRegionHost()
    await http.post(`${regionHost}/api/deal_templates/${dealTemplateId}/disable_org_access`)
  },
}

const fetchRegionDealTemplates = async (
  region: Region,
  userships: DealUsership[],
): Promise<DealTemplateWithUsership[]> => {
  const result = await http.get(`${location.protocol}//${region.host}/api/deal_templates`)
  const templates = parseWithZod(DealTemplatesResponseSchema, result).deal_templates

  return templates.map(template => {
    // Org wide templates are listed without the user holding a usership for them
    const usership = userships.find(u => u.deal_id === template.deal_id)

    return {
      ...template,
      region_host: region.host,
      region_id: region.id,
      usership_id: usership?.id ?? null,
      is_trashed: usership?.is_trashed ?? false,
      user_last_accessed: usership?.last_access ?? null,
      tfa_required: usership?.tfa_required ?? false,
    }
  })
}
